/**
 * Habitia — Esquema de procesos operacionales (Fase 2).
 *
 * Documentación tipada (JSDoc) + metadatos de render del registro de procesos.
 * NO contiene lógica de negocio: cada `ProcessDefinition` describe, en términos de
 * negocio, un proceso reconstruido en `src/processes/PROCESOS.md`. La UI del apartado
 * "Procesos" (Super Admin) lee este registro para dibujar diagramas con carriles por actor.
 *
 * Regla del repo: colores/tipografía siempre desde `designTokens.js`, nunca hardcodeados.
 */

import { colors } from "../utils/designTokens";

// ─── Tipos (JSDoc) ───────────────────────────────────────────────────────────

/** @typedef {'finanzas'|'edificios'|'personas'|'operaciones'|'comunicaciones'|'ia'|'saas'} ProcessCategory */
/** @typedef {'COMPLETO'|'PARCIAL'|'INFERIDO'} ImplStatus */
/** @typedef {'start'|'task'|'decision'|'notification'|'document'|'handoff'|'wait'|'end'} StepType */

/**
 * Carril del diagrama: un actor/rol responsable.
 * @typedef {Object} Lane
 * @property {string} id      Identificador único dentro del proceso (ej: "admin").
 * @property {string} actor   Nombre del actor (debe existir en ACTORS para el color).
 * @property {string} [color] Override opcional; por defecto se deriva con laneColor(actor).
 */

/**
 * Paso operativo del proceso.
 * @typedef {Object} ProcessStep
 * @property {string} id            Único dentro del proceso (ej: "s1").
 * @property {string} laneId        Carril (actor que lo ejecuta) — referencia a Lane.id.
 * @property {StepType} type        Tipo de nodo.
 * @property {string} label         Texto corto del paso.
 * @property {string} [description] Detalle operativo.
 * @property {string} [sla]         Plazo / SLA si existe.
 * @property {string} [evidence]    Referencia mínima al código: "archivo:línea".
 */

/**
 * Transición entre pasos.
 * @typedef {Object} ProcessTransition
 * @property {string} from    Step.id origen.
 * @property {string} to      Step.id destino.
 * @property {string} [label] Condición de negocio (ej: "si pagó en término").
 */

/**
 * Definición completa de un proceso.
 * @typedef {Object} ProcessDefinition
 * @property {string} id
 * @property {string} name
 * @property {ProcessCategory} category
 * @property {string} outcome  Resultado de negocio.
 * @property {string} trigger  Qué lo inicia.
 * @property {string[]} actors Lista de actores involucrados.
 * @property {ImplStatus} status
 * @property {string} [description]
 * @property {Lane[]} lanes
 * @property {ProcessStep[]} steps
 * @property {ProcessTransition[]} transitions
 */

// ─── Categorías ──────────────────────────────────────────────────────────────

/** @type {ProcessCategory[]} Orden de presentación en el selector. */
export const CATEGORIES = [
  "finanzas",
  "edificios",
  "personas",
  "operaciones",
  "comunicaciones",
  "ia",
  "saas",
];

export const CATEGORY_META = {
  finanzas: { label: "Finanzas", accent: colors.success },
  edificios: { label: "Edificios", accent: colors.primary },
  personas: { label: "Personas y acceso", accent: colors.info },
  operaciones: { label: "Operaciones", accent: colors.warning },
  comunicaciones: { label: "Comunicaciones", accent: "#be185d" },
  ia: { label: "Inteligencia artificial", accent: "#6b21a8" },
  saas: { label: "Comercial (SaaS)", accent: colors.primaryDark },
};

// ─── Estado de implementación ─────────────────────────────────────────────────

/** Mapea ImplStatus → clave de status para <StatusBadge> (statusColors.js). */
export const STATUS_META = {
  COMPLETO: { key: "completo", label: "Completo" },
  PARCIAL: { key: "parcial", label: "Parcial" },
  INFERIDO: { key: "inferido", label: "Inferido" },
};

// ─── Tipos de paso (render) ────────────────────────────────────────────────────

/** @type {StepType[]} */
export const STEP_TYPES = [
  "start",
  "task",
  "decision",
  "notification",
  "document",
  "handoff",
  "wait",
  "end",
];

/**
 * Colores por tipo de paso. El ícono lo resuelve la UI (react-icons) para no
 * acoplar el registro a una librería de íconos.
 */
export const STEP_TYPE_META = {
  start: { label: "Inicio", bg: colors.successLight, color: colors.successDark, border: "#86efac" },
  task: { label: "Tarea", bg: colors.primaryLighter, color: colors.primaryDark, border: colors.primaryLight },
  decision: { label: "Decisión", bg: colors.warningLight, color: colors.warningDark, border: "#fcd34d" },
  notification: { label: "Notificación", bg: "#f3e8ff", color: "#6b21a8", border: "#d8b4fe" },
  document: { label: "Documento", bg: colors.gray100, color: colors.gray700, border: colors.gray300 },
  handoff: { label: "Handoff", bg: colors.infoLight, color: colors.infoDark, border: "#93c5fd" },
  wait: { label: "Espera / SLA", bg: "#fff7ed", color: "#9a3412", border: "#fdba74" },
  end: { label: "Fin", bg: colors.black, color: colors.white, border: colors.black },
};

// ─── Actores y colores de carril ───────────────────────────────────────────────

/** Actores canónicos (usados en las definiciones). */
export const ACTORS = [
  "Super Admin",
  "Administrador",
  "Residente",
  "Propietario",
  "Inquilino",
  "Empleado",
  "Proveedor",
  "Bot Habi",
  "Sistema",
  "Visitante",
  "Meta/WhatsApp",
  "Pasarela de pago",
];

const LANE_PALETTE = [
  { bg: colors.primarySubtle, border: colors.primaryLight, text: colors.primaryDark }, // 0 azul
  { bg: "#f0fdf4", border: "#86efac", text: colors.successDark }, // 1 verde
  { bg: colors.warningLight, border: "#fcd34d", text: colors.warningDark }, // 2 ámbar
  { bg: "#faf5ff", border: "#d8b4fe", text: "#6b21a8" }, // 3 violeta
  { bg: "#fdf2f8", border: "#f9a8d4", text: "#be185d" }, // 4 rosa
  { bg: colors.gray100, border: colors.gray300, text: colors.gray700 }, // 5 gris
  { bg: "#ecfeff", border: "#67e8f9", text: "#155e75" }, // 6 cian
];

const ACTOR_COLOR = {
  "Super Admin": LANE_PALETTE[3],
  Administrador: LANE_PALETTE[0],
  Residente: LANE_PALETTE[1],
  Propietario: LANE_PALETTE[1],
  Inquilino: LANE_PALETTE[4],
  Empleado: LANE_PALETTE[6],
  Proveedor: LANE_PALETTE[2],
  "Bot Habi": LANE_PALETTE[3],
  Sistema: LANE_PALETTE[5],
  Visitante: LANE_PALETTE[4],
  "Meta/WhatsApp": LANE_PALETTE[1],
  "Pasarela de pago": LANE_PALETTE[2],
};

/**
 * Color del carril para un actor. Determinístico: usa el mapa y, si no está,
 * cae a la paleta por suma de caracteres (estable entre renders).
 * @param {string} actor
 * @returns {{ bg: string, border: string, text: string }}
 */
export function laneColor(actor) {
  if (actor && ACTOR_COLOR[actor]) return ACTOR_COLOR[actor];
  let h = 0;
  const s = actor || "";
  for (let i = 0; i < s.length; i++) h = (h + s.charCodeAt(i)) % LANE_PALETTE.length;
  return LANE_PALETTE[h];
}
